import { z } from "zod";

// TES-563 — weight-related comorbidity in F-02, and the BMI 27–30 rule.
//
//  (canonical F-02 document, "KANONISK KORRIGERING —
// SAMSJUKLIGHET VID BMI 27–30"): a patient with BMI ≥27 and <30 may not be
// approved automatically without at least one CURRENT weight-related
// comorbidity from the qualifying core list. F-02 therefore records, per
// selected condition, whether it is current, past or uncertain; a past
// condition never qualifies. PCOS, fatty liver, joint disease, gout and
// "other" are kept as clinical information but do not open the branch on
// their own. Shared here so the API evaluator and the admin assessment
// block (TES-564) agree by construction, not by convention.

/** `med_conditions` option values in F-02 (intake v6+). */
export const MED_CONDITION_VALUES = [
  "typ2_diabetes",
  "prediabetes",
  "hogt_blodtryck",
  "blodfetter",
  "hjart_karl",
  "stroke_tia",
  "somnapne",
  "pcos",
  "fettlever",
  "ledsjukdom",
  "gikt",
  "annat",
] as const;
export type MedConditionValue = (typeof MED_CONDITION_VALUES)[number];

/** The qualifying core list for the BMI 27–30 branch (order as in the document). */
export const BMI_27_30_QUALIFYING_CONDITIONS: readonly MedConditionValue[] = [
  "prediabetes",
  "typ2_diabetes",
  "hogt_blodtryck",
  "blodfetter",
  "somnapne",
  "hjart_karl",
  "stroke_tia",
];

export const CONDITION_STATUSES = ["aktuellt", "tidigare", "osaker"] as const;
export type ConditionStatus = (typeof CONDITION_STATUSES)[number];

/** The per-condition status question key, e.g. `med_condition_status_prediabetes`. */
export function conditionStatusKey(condition: MedConditionValue): string {
  return `med_condition_status_${condition}`;
}

export const BMI_27_30_RANGE = { min: 27, max: 30 } as const;

/** BMI from the answers' own weight (kg) and height (cm); null when either is missing or absurd. */
export function bmiFromAnswers(
  answers: Record<string, unknown>,
): number | null {
  const kg = Number(answers["med_current_weight"]);
  const cm = Number(answers["med_height"]);
  if (!Number.isFinite(kg) || !Number.isFinite(cm) || kg <= 0 || cm <= 0)
    return null;
  return kg / Math.pow(cm / 100, 2);
}

/** Selected core-list conditions the patient marked as CURRENT. */
export function currentQualifyingConditions(
  answers: Record<string, unknown>,
): MedConditionValue[] {
  const selected = answers["med_conditions"];
  if (!Array.isArray(selected)) return [];
  return BMI_27_30_QUALIFYING_CONDITIONS.filter(
    (c) =>
      selected.includes(c) && answers[conditionStatusKey(c)] === "aktuellt",
  );
}

/**
 * True when the BMI 27–30 branch lacks its precondition: BMI in range and no
 * core-list condition marked current. A missing status (older submissions)
 * counts as not current — the automation may only block, never approve.
 */
export function bmi27to30WithoutCurrentComorbidity(
  answers: Record<string, unknown>,
): boolean {
  const bmi = bmiFromAnswers(answers);
  if (bmi === null) return false;
  if (bmi < BMI_27_30_RANGE.min || bmi >= BMI_27_30_RANGE.max) return false;
  return currentQualifyingConditions(answers).length === 0;
}

// ---------------------------------------------------------------------------
// TES-564 — the doctor's assessment block: the patient's F-02 answers per
// condition, joined with the doctor's verification row and the BMI with its
// provenance. The read DTO is derived per request (the answers are the fact;
// the verification row is the only stored part).

export const COMORBIDITY_VERIFICATION_STATUSES = [
  "EJ_GRANSKAD",
  "VERIFIERAD",
  "OKLAR",
] as const;
export const comorbidityVerificationStatusSchema = z.enum(
  COMORBIDITY_VERIFICATION_STATUSES,
);
export type ComorbidityVerificationStatus = z.infer<
  typeof comorbidityVerificationStatusSchema
>;

export const COMORBIDITY_EVIDENCE_SOURCES = [
  "PATIENTUPPGIFT",
  "LAKEMEDELSLISTA",
  "BLODTRYCK",
  "PROVSVAR",
  "JOURNAL_VARDKALLA",
  "ANNAT",
] as const;
export const comorbidityEvidenceSourceSchema = z.enum(
  COMORBIDITY_EVIDENCE_SOURCES,
);
export type ComorbidityEvidenceSource = z.infer<
  typeof comorbidityEvidenceSourceSchema
>;

export const conditionStatusSchema = z.enum(CONDITION_STATUSES);

/** PUT employee-api/v1/patients/:id/comorbidity-verification — one condition per call. */
export const comorbidityVerificationUpdateSchema = z
  .object({
    conditionKey: z.enum(MED_CONDITION_VALUES),
    status: comorbidityVerificationStatusSchema,
    // Required once the doctor has looked (VERIFIERAD / OKLAR); meaningless
    // — and therefore dropped — while EJ_GRANSKAD.
    evidenceSource: comorbidityEvidenceSourceSchema.nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.status !== "EJ_GRANSKAD" && !v.evidenceSource) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["evidenceSource"],
        message: "evidence_source_required",
      });
    }
  });
export type ComorbidityVerificationUpdate = z.infer<
  typeof comorbidityVerificationUpdateSchema
>;

export const comorbidityConditionAssessmentSchema = z.object({
  conditionKey: z.enum(MED_CONDITION_VALUES),
  /** What the patient answered on the status question; null on pre-v6 intakes. */
  patientStatus: conditionStatusSchema.nullable(),
  /** In BMI_27_30_QUALIFYING_CONDITIONS. */
  coreList: z.boolean(),
  /** Only the free text for `annat`; null for every other condition. */
  otherText: z.string().nullable(),
  verification: z.object({
    status: comorbidityVerificationStatusSchema,
    evidenceSource: comorbidityEvidenceSourceSchema.nullable(),
    verifiedById: z.string().nullable(),
    verifiedByName: z.string().nullable(),
    verifiedAt: z.string().nullable(),
  }),
});
export type ComorbidityConditionAssessment = z.infer<
  typeof comorbidityConditionAssessmentSchema
>;

export const comorbidityAssessmentSchema = z.object({
  intake: z
    .object({
      submissionId: z.string(),
      templateVersion: z.number().int(),
      submittedAt: z.string(),
    })
    .nullable(),
  bmi: z.object({
    value: z.number().nullable(),
    inRange27to30: z.boolean(),
    weightKg: z.number().nullable(),
    // 'measurement:<CoachMeasurementSource>' = her current weight in the
    // measurement series (TES-1249 part 2; 'weight_log:<source>' before it);
    // 'intake' = the F-02 answer when nothing is measured.
    weightSource: z.string().nullable(),
    weightAt: z.string().nullable(),
    heightCm: z.number().nullable(),
    heightAt: z.string().nullable(),
  }),
  /** Only the conditions the patient selected, in MED_CONDITION_VALUES order. */
  conditions: z.array(comorbidityConditionAssessmentSchema),
  /** True when BMI is in range and no core-list condition is patient-current AND doctor-verified. */
  lacksVerifiedCurrentComorbidity: z.boolean(),
});
export type ComorbidityAssessment = z.infer<typeof comorbidityAssessmentSchema>;
